import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { startQrScanner, stopQrScanner } from "@/lib/qrscan";
import { CheckCircle2, AlertCircle, ScanLine, Calendar, MapPin } from "lucide-react";
import api from "@/lib/api";

const RESET_MS = 7000;

const chime = (ok) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = ok ? 880 : 220;
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
  } catch {}
};

const pretty = (iso) =>
  iso ? new Date(iso).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" }) : "";

const HEX = /^#[0-9a-fA-F]{6}$/;
const hex = (value, fallback) => (HEX.test(value || "") ? value : fallback);

// Perceived brightness, so a light background can take darker accents and stay readable.
const isLight = (color) => {
  const n = parseInt(color.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
};

const paletteFor = (bg, text) => {
  const light = isLight(bg);
  return {
    bg,
    text,
    accent: light ? "#0b7a58" : "#00e599",
    warn: light ? "#a15c00" : "#ffb800",
    bad: light ? "#b91c1c" : "#f87171",
    reader: light ? "#e9edf2" : "#141a23",
    wash: light ? 0.14 : 0.2,
    fade: `linear-gradient(to top, ${bg}, ${bg}d9, ${bg}99)`,
  };
};

export default function Kiosk() {
  const { eventId } = useParams();
  const [screen, setScreen] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [gunValue, setGunValue] = useState("");
  const scannerRef = useRef(null);
  const gunRef = useRef(null);
  const busyRef = useRef(false);
  const resetRef = useRef(null);

  // The kiosk is a guest display and always renders dark (see .kiosk-screen in index.css).
  useEffect(() => {
    api
      .get(`/public/kiosk/${eventId}`)
      .then(({ data }) => setScreen(data))
      .catch(() => setError("This welcome screen is not available. Please see our team at the desk."));
  }, [eventId]);

  const submit = useCallback(
    async (token) => {
      if (busyRef.current || !token) return;
      busyRef.current = true;
      try {
        const { data } = await api.post(`/public/kiosk/${eventId}/scan`, { qr_token: token.trim() });
        setResult(data);
        chime(data.result === "success");
      } catch {
        setResult({ result: "invalid", line: "Something went wrong. Please see our team at the desk." });
        chime(false);
      } finally {
        clearTimeout(resetRef.current);
        resetRef.current = setTimeout(() => setResult(null), RESET_MS);
        setTimeout(() => (busyRef.current = false), 1500);
      }
    },
    [eventId]
  );

  // Camera scanning starts by itself so the screen can be left unattended.
  useEffect(() => {
    if (!screen || scannerRef.current) return;
    let cancelled = false;
    // Small delay so a quick remount never leaves a half-started camera behind.
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const scanner = await startQrScanner("kiosk-reader", (text) => submit(text));
        scannerRef.current = scanner;
        if (cancelled) await stopQrScanner(scanner);
      } catch (e) {
        console.warn("Kiosk camera unavailable", e);
      }
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopQrScanner(scannerRef.current);
      scannerRef.current = null;
    };
  }, [screen, submit]);

  // A barcode scanner types the pass then presses Enter, so the hidden field keeps focus.
  useEffect(() => {
    const claim = () => gunRef.current?.focus();
    claim();
    const id = setInterval(claim, 1500);
    return () => clearInterval(id);
  }, []);

  if (error)
    return (
      <div
        className="fixed inset-0 kiosk-screen grid place-items-center p-8 text-center"
        style={{ backgroundColor: "#ffffff", color: "#0f172a" }}
      >
        <p className="text-xl" data-testid="kiosk-error">{error}</p>
      </div>
    );

  if (!screen)
    return (
      <div
        className="fixed inset-0 kiosk-screen"
        style={{ backgroundColor: "#ffffff" }}
        data-testid="kiosk-loading"
      />
    );

  const { event, kiosk_page: page } = screen;
  const ok = result?.result === "success";
  const repeat = result?.result === "duplicate";
  const c = paletteFor(hex(page.bg_color, "#ffffff"), hex(page.text_color, "#0f172a"));

  return (
    <div
      className="fixed inset-0 kiosk-screen overflow-auto"
      style={{ backgroundColor: c.bg, color: c.text }}
      data-testid="kiosk-page"
    >
      {event.kiosk_bg ? (
        <>
          <img
            src={event.kiosk_bg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="kiosk-background"
          />
          <div
            className="absolute inset-0"
            style={{ backgroundColor: c.bg, opacity: (page.bg_dim ?? 40) / 100 }}
          />
        </>
      ) : (
        <>
          {event.key_visual && (
            <img
              src={event.key_visual}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: c.wash }}
            />
          )}
          <div className="absolute inset-0" style={{ background: c.fade }} />
        </>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const v = gunValue;
          setGunValue("");
          submit(v);
        }}
        className="absolute opacity-0 pointer-events-none"
      >
        <input
          ref={gunRef}
          data-testid="kiosk-scan-input"
          autoComplete="off"
          value={gunValue}
          onChange={(e) => setGunValue(e.target.value)}
        />
      </form>

      <div className="relative min-h-full flex flex-col items-center justify-center px-6 py-16 text-center">
        {/* The reader stays mounted while a result is on screen, otherwise the camera would die
            after the first guest. */}
        <div
          className={result ? "absolute w-px h-px overflow-hidden opacity-0 pointer-events-none" : "rise"}
          data-testid={result ? "kiosk-idle-parked" : "kiosk-idle"}
        >
            <p
              className="font-mono2 text-xs uppercase tracking-[0.3em]"
              style={{ color: c.accent }}
              data-testid="kiosk-host"
            >
              {event.host_org || "Welcome"}
            </p>
            <h1
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-none mt-4"
              data-testid="kiosk-event-name"
            >
              {event.name}
            </h1>
            <div
              className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2 mt-7 text-sm"
              style={{ color: c.text, opacity: 0.75 }}
            >
              <span className="flex items-center gap-2" data-testid="kiosk-event-date">
                <Calendar size={15} /> {pretty(event.date)} · {event.time}
              </span>
              <span className="flex items-center gap-2" data-testid="kiosk-event-venue">
                <MapPin size={15} /> {event.venue}
              </span>
            </div>

            <div className="mt-14 mx-auto w-[min(420px,80vw)]">
              <div
                id="kiosk-reader"
                className="rounded-2xl overflow-hidden [&_video]:w-full [&_video]:h-auto"
                style={{ backgroundColor: c.reader }}
              />
            </div>
            <p
              className="flex items-center justify-center gap-3 text-lg md:text-xl mt-9"
              style={{ color: c.accent }}
              data-testid="kiosk-idle-note"
            >
              <ScanLine size={22} /> {page.idle_note}
            </p>
        </div>

        {result && (
          <div className="rise max-w-3xl" data-testid="kiosk-result">
            {ok || repeat ? (
              <CheckCircle2 size={72} className="mx-auto" style={{ color: repeat ? c.warn : c.accent }} />
            ) : (
              <AlertCircle size={72} className="mx-auto" style={{ color: c.bad }} />
            )}

            {repeat && (
              <p
                className="font-mono2 text-xs uppercase tracking-[0.3em] mt-7"
                style={{ color: c.warn }}
                data-testid="kiosk-already"
              >
                Already checked in
              </p>
            )}

            {result.greeting && (
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-6"
                data-testid="kiosk-greeting"
              >
                {result.greeting}
              </h1>
            )}
            <p className="text-lg md:text-xl mt-6" style={{ opacity: 0.85 }} data-testid="kiosk-line">
              {result.line}
            </p>
            {result.reference && (
              <p
                className="font-mono2 text-xs mt-8"
                style={{ opacity: 0.55 }}
                data-testid="kiosk-reference"
              >
                REF {result.reference}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
